import * as T from '@radix-ui/react-tabs';
import { cn } from '@/lib/cn';

/**
 * Tabs — las pestañas de una pantalla.
 *
 * `@radix-ui/react-tabs` llevaba instalado en `package.json` desde que se montó
 * la capa React y **no lo usaba nadie**: era peso descargado sin nada que
 * mostrara. Esta pantalla lo estrena.
 *
 * Por qué Radix y no cinco `<button>` con una variable de estado, que es lo que
 * hacía la versión vanilla: lo que se pierde escribiéndolo a mano no es el
 * aspecto, es el teclado. Radix trae el patrón completo de la WAI-ARIA —flechas
 * para moverse entre pestañas, Home/Fin, `role="tab"`, `aria-selected`,
 * `aria-controls` y el foco puesto donde toca—. La versión vanilla eran botones
 * sueltos con `.active`: un lector de pantalla no anunciaba que fueran pestañas
 * ni cuántas había, y las flechas no hacían nada.
 *
 * ## El acento no pinta la pestaña activa
 *
 * Se marca con **peso y filete inferior en navy**, no con el naranja de marca.
 * El reparto del 2026-09-03 le da al acento un solo trabajo dentro de la
 * aplicación —dónde estás en la barra lateral, y las barras de progreso—, y una
 * pestaña activa ya se distingue sin gastarlo. Lo vigila
 * `tests/test_reparto_del_acento.py`.
 *
 * ## No anima la maquetación
 *
 * El filete se mueve con `transform`, no cambiando `border-width` ni `padding`.
 * El empujón de las filas de la portada se escribió una vez con `padding-left`,
 * se veía idéntico y el navegador recalculaba la caja en cada fotograma.
 */

export interface Pestana {
  /** La clave que viaja en la sub-ruta del hash. */
  clave: string;
  etiqueta: string;
  /** Clase de Bootstrap Icons, p. ej. `bi-grid`. */
  icono?: string;
  /** Un número al lado de la etiqueta —lo que espera decisión, lo que falta—.
   *  `0` y `undefined` no pintan nada: un contador en cero enseña a ignorar el
   *  sitio donde algún día habrá un uno. */
  cuenta?: number;
}

export function Tabs({
  pestanas, valor, onCambio, children, className,
}: {
  pestanas: Pestana[];
  valor: string;
  onCambio: (v: string) => void;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <T.Root value={valor} onValueChange={onCambio} className={cn('flex flex-col gap-5', className)}>
      {/* `overflow-x-auto` y no envolver en varias filas: con cinco pestañas a
          375 px, envolver mueve el contenido de sitio cada vez que aparece o
          desaparece una. Deslizar mantiene la página quieta. */}
      <T.List
        className="-mb-px flex gap-1 overflow-x-auto border-b border-line
                   [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      >
        {pestanas.map((p) => (
          <T.Trigger
            key={p.clave}
            value={p.clave}
            className={cn(
              'group relative inline-flex shrink-0 items-center gap-2 whitespace-nowrap',
              'px-4 py-2.5 text-3 font-semibold text-muted',
              'transition-colors hover:text-ink',
              'focus-visible:focus-anillo',
              'data-[state=active]:text-brand-ink',
              'min-h-11',
            )}
          >
            {p.icono && <i className={cn('bi', p.icono)} aria-hidden="true" />}
            {p.etiqueta}
            {p.cuenta ? (
              <span
                aria-hidden="true"
                className="inline-grid min-w-5 place-items-center rounded-full bg-warn/25
                           px-1.5 text-1 font-bold text-warn-ink"
              >
                {p.cuenta}
              </span>
            ) : null}
            {/* El filete de la pestaña activa. Es un hijo con `scale-x`, y no
                un `border-bottom` que aparece y desaparece: un borde que se
                enciende cambia la altura de la caja y empuja el contenido. */}
            <span
              aria-hidden="true"
              className="absolute inset-x-2 bottom-0 h-0.5 origin-center scale-x-0 rounded-full
                         bg-brand transition-transform duration-150
                         group-data-[state=active]:scale-x-100
                         motion-reduce:transition-none"
            />
          </T.Trigger>
        ))}
      </T.List>
      {children}
    </T.Root>
  );
}

/** El contenido de una pestaña. `forceMount` NO se usa: montar las cinco a la
 *  vez dispararía las consultas de todas al entrar, y la de la bandeja pide
 *  cincuenta filas. */
export function TabPanel({ clave, children }: { clave: string; children: React.ReactNode }) {
  return (
    <T.Content value={clave} className="focus-visible:focus-anillo">
      {children}
    </T.Content>
  );
}
